import { Box, Stack, TextField, Typography } from '@mui/material';
import React, { memo, useEffect } from 'react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const DeductionItem = memo(({ label, amount }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.3,
      px: 2,
      borderBottom: '1px dotted #ccc',
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <TrendingDownIcon sx={{ fontSize: 18, color: 'error.light' }} />
      {label}
    </Typography>

    <Typography
      variant="body1"
      sx={{
        color: 'text.secondary',
        fontWeight: 500,
        fontFamily: 'monospace',
      }}
    >
      ₹ {amount?.toLocaleString('en-IN')}
    </Typography>
  </Box>
));

const RefundCalculation = ({ refundData, formik, finalRefundAmount }) => {
  useEffect(() => {
    if (formik) {
      formik.setFieldValue('refundToBePaid', refundData.net_refund_amount || 0);
    }
  }, [refundData]);
  return (
    <Box
      sx={{
        borderRadius: 2,
        mb: 2,
        backgroundColor: '#fff',
        overflow: 'hidden',
        boxShadow: '0px 1px 4px rgba(0,0,0,0.16)',
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          p: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          Refund Calculation Breakdown
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Stack direction="column" spacing={1.5} mt={2}>
          <Box display="flex" gap={1}>
            <Typography fontWeight={600}>Days Since Payment :</Typography>
            <Typography>{refundData?.days_between} Days</Typography>
          </Box>

          <DeductionItem label="Total Fee" amount={refundData?.total_fee} />
          {/* <DeductionItem label="Collected Fee" amount={refundData?.collected_fee} /> */}
          <DeductionItem label="Deduction - Session Completed" amount={refundData?.session_completed_deduction} />
          <DeductionItem label="Deduction - GST (18%)" amount={refundData?.gst_deduction} />
          <DeductionItem label="Deduction - EMI" amount={refundData?.emi_deduction} />
          <DeductionItem label="Deduction - Admission Fee (10%)" amount={refundData?.admission_fee_deduction} />
          <DeductionItem label="Deduction - Books and Courier" amount={refundData?.books_and_courier} />

          <Box
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              borderRadius: 3,
              px: 3,
              py: 1,
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                Net Refund Amount
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mt: 0.5,
                  fontFamily: 'monospace',
                }}
              >
                ₹ {refundData?.net_refund_amount}
              </Typography>
            </Box>

            <AccountBalanceWalletIcon sx={{ fontSize: 30, opacity: 0.4 }} />
          </Box>

          {Boolean(finalRefundAmount) && (
            <Box
              sx={{
                background: 'linear-gradient(135deg, #76ce17ff 0%, #c0dda1ff 100%)',
                borderRadius: 3,
                px: 3,
                py: 1,
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.9,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  Finalized Refund Amount
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                    fontFamily: 'monospace',
                  }}
                >
                  ₹ {finalRefundAmount}
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>
      </Box>

      {formik && (
        <Stack spacing={1} sx={{ p: 1, pt: 0, mt: 1 }}>
          <TextField
            type="number"
            fullWidth
            label="To be paid (If Applicable)"
            name="refundToBePaid"
            placeholder="Enter amount to be paid"
            value={formik.values.refundToBePaid}
            onChange={formik.handleChange}
            error={formik.touched.refundToBePaid && Boolean(formik.errors.refundToBePaid)}
            helperText={formik.touched.refundToBePaid && formik.errors.refundToBePaid}
          />
          <TextField
            fullWidth
            label="Refund Reason"
            name="refundReason"
            rows={2}
            placeholder="Type your remarks here..."
            multiline
            value={formik.values.refundReason}
            onChange={formik.handleChange}
            error={formik.touched.refundReason && Boolean(formik.errors.refundReason)}
            helperText={formik.touched.refundReason &&formik.errors.refundReason}
          />
        </Stack>
      )}
    </Box>
  );
};

export default RefundCalculation;
